import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Save, Camera, Trash2, Copy, Eye, EyeOff, Globe, Lock,
  User, Image as ImageIcon, ExternalLink, Check, X,
} from "lucide-react";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import api, { formatApiError } from "@/lib/api";
// Signup Redesign · CX Gate — NationalTeamCombobox intentionally
// removed. The favorite national team field is a plain <input> so
// collectors can enter any team without being forced into an enum.

const USERNAME_RE = /^[a-z0-9_-]{3,32}$/;

// ═════════════════════════════════════════════════════════════
// Page
// ═════════════════════════════════════════════════════════════
export default function CollectorProfileSettings() {
  const { collector, refresh } = useCollectorAuth();
  return (
    <div className="space-y-6" data-testid="profile-settings-page">
      <div>
        <div className="kicker kicker-red">Collector Profile</div>
        <h1 className="h-display text-3xl md:text-4xl mt-1" data-testid="profile-settings-heading">
          Your public collector identity.
        </h1>
        <p className="text-sm text-[var(--smoke)] mt-2 max-w-2xl">
          Configure how you appear on CollectorUp. New profiles are <strong>public by default</strong> —
          flip the master switch off any time to make your library private. You control which sections,
          statuses, and individual albums are visible.
        </p>
      </div>

      <VisibilityCard collector={collector} onSaved={refresh} />
      <IdentityCard collector={collector} onSaved={refresh} />
      <EmailPreferencesCard collector={collector} onSaved={refresh} />
      <ImagesCard collector={collector} onSaved={refresh} />
      <PublicSectionsCard collector={collector} onSaved={refresh} />
      <AlbumVisibilityCard collector={collector} onSaved={refresh} />
    </div>
  );
}


// ═════════════════════════════════════════════════════════════
// E8C · Email Preferences (marketing_consent_email toggle)
// ═════════════════════════════════════════════════════════════
function EmailPreferencesCard({ collector, onSaved }) {
  // E8C.2 · Seed state directly from the collector object so the
  // toggle can't briefly flash OFF for a user whose stored consent
  // is TRUE. `null` means "still hydrating" — the button is
  // disabled in that window so an accidental click can't swallow
  // an opt-out.
  const seeded = (collector && Object.prototype.hasOwnProperty.call(collector, "marketing_consent_email"))
    ? (typeof collector.marketing_consent_email === "boolean"
        ? collector.marketing_consent_email
        : null)
    : null;
  const [current, setCurrent] = useState(seeded);
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(seeded !== null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/collector-auth/me/marketing-consent-status");
        if (cancelled) return;
        const cur = r.data?.current;
        setCurrent(typeof cur === "boolean" ? cur : false);
      } catch {
        if (!cancelled && seeded === null) setCurrent(false);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [collector?.id, seeded]);

  const on = current === true;
  const canClick = hydrated && !busy;

  const flip = async () => {
    if (!canClick) return;   // E8C.2 · never send a stale flip
    const next = !on;
    setBusy(true);
    setCurrent(next);   // optimistic
    try {
      await api.post("/collector-auth/me/marketing-consent",
        { consent: next, source: "profile_settings" });
      toast.success(next
        ? "You'll receive helpful CollectorUp updates."
        : "Preference saved. We won't send engagement emails.");
      await onSaved?.();
    } catch (err) {
      setCurrent(!next);  // rollback
      toast.error(formatApiError(err?.response?.data?.detail)
        || "Could not update your preference.");
    } finally { setBusy(false); }
  };

  return (
    <Card testid="profile-email-preferences-card">
      <CardHeader Icon={Globe} kicker="Email preferences"
        title="Helpful CollectorUp updates"
        subtitle="Occasional reminders about your albums, missing stickers, duplicates and collecting progress. Turn this off any time." />
      <div className="mt-5">
        <button
          type="button"
          onClick={flip}
          disabled={!canClick}
          aria-pressed={on}
          aria-busy={!hydrated}
          data-testid="profile-marketing-consent-toggle"
          data-hydrated={hydrated ? "1" : "0"}
          className={`text-left rounded-xl border-2 border-[var(--ink)] px-4 py-3 flex items-start gap-3 transition w-full ${
            on ? "bg-[var(--panini-yellow-soft)]" : "bg-white"
          } disabled:opacity-50`}
        >
          <div className={`mt-0.5 w-10 h-6 rounded-full border-2 border-[var(--ink)] relative shrink-0 ${on ? "bg-[var(--panini-red)]" : "bg-[var(--stone)]"}`}>
            <div className={`absolute top-0 left-0 w-5 h-5 rounded-full bg-white border-2 border-[var(--ink)] transition-transform ${on ? "translate-x-4" : ""}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold" data-testid="profile-marketing-consent-label">
              {!hydrated ? "Loading preference…"
                : on ? "Enabled — you'll get helpful updates"
                : "Disabled — no engagement emails"}
            </div>
            <div className="text-[11px] text-[var(--smoke)] mt-0.5">
              We never send marketing emails to disabled accounts. Order and
              account emails aren&apos;t affected by this toggle.
            </div>
          </div>
        </button>
      </div>
    </Card>
  );
}


// ═════════════════════════════════════════════════════════════
// Visibility (private / public + preview / copy link)
// ═════════════════════════════════════════════════════════════
function VisibilityCard({ collector, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [localPublic, setLocalPublic] = useState(null); // optimistic override
  const backendPublic = !!collector?.profile_public;
  const isPublic = localPublic !== null ? localPublic : backendPublic;
  const username = collector?.username || "";
  const publicUrl = username ? `${window.location.origin}/collectors/${username}` : "";

  // Reset optimistic state whenever the collector context catches up.
  useEffect(() => { setLocalPublic(null); }, [backendPublic]);

  const toggle = async () => {
    if (isPublic) {
      if (!window.confirm("Make your profile private again? Your public URL will stop working until you re-enable it.")) return;
    }
    const next = !isPublic;
    setLocalPublic(next);  // immediate visual feedback
    setBusy(true);
    try {
      await api.patch("/me/profile/privacy", { profile_public: next });
      toast.success(next ? "Profile is now public." : "Profile is now private.");
      await onSaved?.();
    } catch (err) {
      setLocalPublic(null);  // rollback
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update visibility.");
    } finally { setBusy(false); }
  };
  const copy = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Public profile link copied.");
  };

  return (
    <Card testid="profile-visibility-card">
      <CardHeader
        Icon={isPublic ? Globe : Lock}
        kicker="Profile visibility"
        title={isPublic ? "Public profile" : "Private profile"}
        subtitle={
          isPublic
            ? "Anyone with your link can see the sections you enabled below."
            : "Only you can see your profile. Enable public visibility to share it."
        }
      />
      <div className="mt-5 flex flex-col md:flex-row md:items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          disabled={busy || !username}
          data-testid="profile-visibility-toggle"
          className={`text-sm font-bold rounded-full px-4 py-2 border-2 inline-flex items-center gap-2 transition ${
            isPublic
              ? "bg-emerald-100 text-emerald-900 border-emerald-900"
              : "bg-white text-[var(--ink)] border-[var(--ink)]"
          } disabled:opacity-50`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : (isPublic ? <Globe size={14} /> : <Lock size={14} />)}
          {isPublic ? "Public" : "Private"}
        </button>
        {!username && (
          <div className="text-xs text-[var(--panini-red)] font-bold" data-testid="profile-visibility-no-username">
            Choose a username below before enabling public visibility.
          </div>
        )}
        <div className="flex-1" />
        <a
          href={username ? `/collectors/${username}` : "#"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="profile-preview-link"
          className={`btn-ghost text-sm ${!username ? "opacity-40 pointer-events-none" : ""}`}
        >
          <ExternalLink size={13} /> Preview public profile
        </a>
        {isPublic && (
          <button type="button" onClick={copy} data-testid="profile-copy-link" className="btn-red text-sm">
            <Copy size={13} /> Copy link
          </button>
        )}
      </div>
      {publicUrl && (
        <div className="mt-3 text-xs text-[var(--smoke)] font-mono truncate" data-testid="profile-public-url">
          {publicUrl}
        </div>
      )}
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════
// Identity (display name, username slug, bio, location, category)
// ═════════════════════════════════════════════════════════════
function IdentityCard({ collector, onSaved }) {
  const [displayName, setDisplayName] = useState(collector?.display_name || "");
  const [username, setUsername] = useState(collector?.username || "");
  const [bio, setBio] = useState(collector?.bio || "");
  const [location, setLocation] = useState(collector?.general_location || "");
  // Phase 1e.1 — new "Who You Are" fields (replace Favorite Category in the UI).
  const [club, setClub] = useState(collector?.favorite_club_team || "");
  const [country, setCountry] = useState(collector?.favorite_national_team || "");
  const [ig, setIg] = useState(collector?.instagram_handle || extractIg(collector?.social_urls?.instagram));
  const [tt, setTt] = useState(collector?.tiktok_handle || "");
  // Signup Redesign · CX Gate — collecting-since dropdown.
  const [since, setSince] = useState(collector?.collecting_since || "");
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDisplayName(collector?.display_name || "");
    setUsername(collector?.username || "");
    setBio(collector?.bio || "");
    setLocation(collector?.general_location || "");
    setClub(collector?.favorite_club_team || "");
    setCountry(collector?.favorite_national_team || "");
    setIg(collector?.instagram_handle || extractIg(collector?.social_urls?.instagram));
    setTt(collector?.tiktok_handle || "");
    setSince(collector?.collecting_since || "");
  }, [collector]);

  // Debounced slug availability check.
  const currentUsername = collector?.username || "";
  useEffect(() => {
    const u = username.trim().toLowerCase();
    if (!u) { setAvailability(null); return; }
    if (u === currentUsername.toLowerCase()) { setAvailability({ available: true, current: true }); return; }
    if (!USERNAME_RE.test(u)) {
      setAvailability({ available: false, reason: "format",
        message: "3–32 letters, numbers, underscore or dash." });
      return;
    }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/collector-auth/username-check", { params: { u } });
        setAvailability(r.data);
      } catch (e) {
        setAvailability(null);
      } finally { setChecking(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [username, currentUsername]);

  const save = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) return toast.error("Display name is required.");
    setBusy(true);
    try {
      const payload = {
        display_name: displayName.trim(),
        bio: bio.trim(),
        general_location: location.trim(),
        // Phase 1e.1 — send the new dedicated fields instead of the legacy
        // favorite_category free-text (kept in the DB but no longer shown).
        favorite_club_team: club.trim(),
        favorite_national_team: country.trim(),
        instagram_handle: ig.trim(),
        tiktok_handle: tt.trim(),
        // Signup Redesign · CX Gate — persist collecting-since choice.
        // Empty string is the "Prefer not to say" sentinel that the
        // backend normalizes to null.
        collecting_since: since || 0,
      };
      if (username.trim() && username.trim().toLowerCase() !== currentUsername.toLowerCase()) {
        payload.username = username.trim().toLowerCase();
      }
      await api.patch("/me/profile", payload);
      toast.success("Profile updated.");
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save profile.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="profile-identity-card"
          className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[4px_4px_0_var(--ink)]">
      <CardHeader Icon={User} kicker="Identity" title="Who you are"
        subtitle="These fields appear on your public profile — only when you enable the matching visibility toggles." />

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <Field label="Display name *">
          <TextInput value={displayName} onChange={setDisplayName} maxLength={80} testid="profile-display-name" />
        </Field>
        <Field label="Username / profile slug">
          <div>
            <div className="flex items-stretch">
              <span className="inline-flex items-center border-2 border-r-0 border-[var(--ink)] rounded-l-lg px-3 bg-[var(--cream)] text-sm text-[var(--smoke)] font-mono">
                /collectors/
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                data-testid="profile-username"
                maxLength={32}
                placeholder="your-handle"
                className="flex-1 border-2 border-[var(--ink)] rounded-r-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
              />
            </div>
            <SlugStatus availability={availability} checking={checking} />
          </div>
        </Field>
        <Field label="Short bio">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            data-testid="profile-bio"
            rows={3}
            maxLength={280}
            className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="General location">
            <TextInput value={location} onChange={setLocation} maxLength={80} testid="profile-location" placeholder="Miami, FL" />
          </Field>
          {/* Phase 1e.1 — dedicated favorite team fields (replaces Favorite Category) */}
          <Field label="Favorite club team">
            <TextInput value={club} onChange={setClub} maxLength={80} testid="profile-club" placeholder="FC Barcelona" />
          </Field>
          <Field label="Favorite national team">
            {/* Signup Redesign · CX Gate — plain unrestricted text so
                collectors can enter any team (Faroe Islands, etc.)
                without being forced into a preset list. */}
            <TextInput value={country} onChange={setCountry} maxLength={80}
                          testid="profile-national" placeholder="e.g. Ecuador, Morocco, Faroe Islands…" />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Signup Redesign · CX Gate — collecting-since dropdown
              must match the approved 17-option enum exactly. */}
          <Field label="How long have you been collecting?">
            <CollectingSinceSelect value={since} onChange={setSince} />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Instagram handle">
            <div className="flex items-stretch">
              <span className="inline-flex items-center border-2 border-r-0 border-[var(--ink)] rounded-l-lg px-3 bg-[var(--cream)] text-sm text-[var(--smoke)] font-mono">@</span>
              <input
                type="text"
                value={ig}
                onChange={(e) => setIg(e.target.value.replace(/^@/, ""))}
                data-testid="profile-instagram"
                maxLength={30}
                placeholder="yourhandle"
                className="flex-1 border-2 border-[var(--ink)] rounded-r-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
              />
            </div>
          </Field>
          {/* Phase 1e.1 — TikTok handle */}
          <Field label="TikTok handle">
            <div className="flex items-stretch">
              <span className="inline-flex items-center border-2 border-r-0 border-[var(--ink)] rounded-l-lg px-3 bg-[var(--cream)] text-sm text-[var(--smoke)] font-mono">@</span>
              <input
                type="text"
                value={tt}
                onChange={(e) => setTt(e.target.value.replace(/^@/, ""))}
                data-testid="profile-tiktok"
                maxLength={30}
                placeholder="yourhandle"
                className="flex-1 border-2 border-[var(--ink)] rounded-r-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
              />
            </div>
          </Field>
        </div>
        <Field label="Member since">
          <div data-testid="profile-member-since" className="text-sm font-bold border-2 border-dashed border-[var(--ink)] rounded-lg px-3 py-2 bg-[var(--cream)]">
            {collector?.created_at ? new Date(collector.created_at).toLocaleDateString() : "—"}
          </div>
        </Field>
      </div>

      <div className="mt-6 flex justify-end">
        <button type="submit" disabled={busy} data-testid="profile-identity-save" className="btn-red text-sm">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save identity
        </button>
      </div>
    </form>
  );
}

function SlugStatus({ availability, checking }) {
  if (checking) {
    return <div className="mt-1 text-[11px] text-[var(--smoke)] flex items-center gap-1" data-testid="slug-checking">
      <Loader2 size={11} className="animate-spin" /> Checking…
    </div>;
  }
  if (!availability) return null;
  if (availability.current) {
    return <div className="mt-1 text-[11px] text-emerald-800 flex items-center gap-1" data-testid="slug-status-current">
      <Check size={11} /> This is your current username.
    </div>;
  }
  if (availability.available) {
    return <div className="mt-1 text-[11px] text-emerald-800 flex items-center gap-1" data-testid="slug-status-available">
      <Check size={11} /> Available!
    </div>;
  }
  return <div className="mt-1 text-[11px] text-[var(--panini-red)] flex items-center gap-1" data-testid="slug-status-unavailable">
    <X size={11} /> {availability.message || "Not available."}
  </div>;
}

// ═════════════════════════════════════════════════════════════
// Profile / Banner images
// ═════════════════════════════════════════════════════════════
function ImagesCard({ collector, onSaved }) {
  return (
    <Card testid="profile-images-card">
      <CardHeader Icon={ImageIcon} kicker="Photos" title="Profile & banner images"
        subtitle="JPEG · PNG · WebP · Max 5MB. EXIF and metadata are stripped on upload." />
      <div className="mt-5 grid md:grid-cols-2 gap-6">
        <ImageUploader kind="avatar" collector={collector} onSaved={onSaved} testid="profile-avatar-uploader" />
        <ImageUploader kind="banner" collector={collector} onSaved={onSaved} testid="profile-banner-uploader" />
      </div>
    </Card>
  );
}

function ImageUploader({ kind, collector, onSaved, testid }) {
  const isAvatar = kind === "avatar";
  const label = isAvatar ? "Profile image" : "Cover / banner image";
  const endpoint = isAvatar ? "/me/profile/avatar" : "/me/profile/banner";
  const mediaKey = isAvatar ? "avatar_media_id" : "banner_media_id";
  const updatedKey = isAvatar ? "avatar_updated_at" : "banner_updated_at";
  const preview = isAvatar
    ? "rounded-full aspect-square"
    : "rounded-xl aspect-[3/1]";
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const hasImage = !!collector?.[mediaKey];
  const stamp = collector?.[updatedKey] || "";
  const src = hasImage ? `${process.env.REACT_APP_BACKEND_URL}/api${endpoint}?v=${encodeURIComponent(stamp)}` : null;

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image is larger than 5MB.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} updated.`);
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove your ${label.toLowerCase()}?`)) return;
    setBusy(true);
    try {
      await api.delete(endpoint);
      toast.success("Removed.");
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Remove failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</div>
      <div className={`mt-2 ${preview} border-2 border-[var(--ink)] overflow-hidden bg-[var(--panini-yellow-soft)] grid place-items-center relative`}>
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <div className="text-[var(--ink)]/40 text-center px-3">
            <Camera size={24} className="mx-auto" />
            <div className="text-[11px] uppercase tracking-widest font-bold mt-2">No {label.toLowerCase()}</div>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={pick} disabled={busy} className="btn-red text-xs" data-testid={`${testid}-pick`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
          {hasImage ? "Replace" : "Upload"}
        </button>
        {hasImage && (
          <button type="button" onClick={remove} disabled={busy} className="btn-ghost text-xs text-[var(--panini-red)]" data-testid={`${testid}-remove`}>
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// Public sections (which chunks are visible)
// ═════════════════════════════════════════════════════════════
function PublicSectionsCard({ collector, onSaved }) {
  const items = [
    { key: "show_profile_photo", label: "Show profile & banner images", hint: "Otherwise your profile shows the CollectorUp placeholder." },
    { key: "show_bio",           label: "Show bio",                     hint: "" },
    { key: "show_country",       label: "Show general location",        hint: "Shows the location field only — never your exact address." },
    { key: "show_social_links",  label: "Show social links",            hint: "" },
    { key: "show_album_statistics", label: "Show collection statistics", hint: "Aggregated counts only. Private albums are never counted individually." },
    { key: "show_certificates",  label: "Show verified certificates",   hint: "" },
    { key: "show_achievements",  label: "Show achievements & badges",   hint: "" },
    // Iter Album — missing / duplicate list visibility on public profile.
    { key: "show_missing_list",  label: "Show missing stickers list",   hint: "Opt-in. Off by default so your want list stays private until you're ready." },
    { key: "show_duplicate_list",label: "Show duplicate stickers list", hint: "Opt-in. Off by default so your swap pile stays private until you're ready." },
    // Phase 1e.1 — per-field visibility for the new Who-You-Are fields.
    { key: "show_favorite_club_team",     label: "Show favorite club team",     hint: "" },
    { key: "show_favorite_national_team", label: "Show favorite national team", hint: "" },
    { key: "show_instagram",              label: "Show Instagram handle",       hint: "Links to instagram.com/{handle}" },
    { key: "show_tiktok",                 label: "Show TikTok handle",          hint: "Links to tiktok.com/@{handle}" },
  ];
  return (
    <Card testid="profile-sections-card">
      <CardHeader Icon={Eye} kicker="Public sections"
        title="What visitors see"
        subtitle="Only sections you enable appear on your public profile. New accounts start with sensible defaults." />
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        {items.map((it) => <ToggleRow key={it.key} k={it.key} label={it.label} hint={it.hint} collector={collector} onSaved={onSaved} testid={`toggle-${it.key}`} />)}
      </div>
    </Card>
  );
}

function AlbumVisibilityCard({ collector, onSaved }) {
  return (
    <Card testid="profile-album-visibility-card">
      <CardHeader Icon={EyeOff} kicker="Album visibility"
        title="Which album statuses are visible"
        subtitle={<>
          Even with a toggle enabled, individual albums must also be marked <span className="font-bold">Visible on Profile</span> from the{" "}
          <a href="/dashboard/my-collection" className="underline">My Collection</a> page.
        </>} />
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        <ToggleRow k="show_collecting_albums" collector={collector} onSaved={onSaved} label="Show collecting albums"      testid="toggle-show_collecting_albums" />
        <ToggleRow k="show_completed_albums"  collector={collector} onSaved={onSaved} label="Show completed albums"       testid="toggle-show_completed_albums" />
        <ToggleRow k="show_album_only_albums" collector={collector} onSaved={onSaved} label="Show Album Only collections" hint="Albums kept unstickered." testid="toggle-show_album_only_albums" />
        <ToggleRow k="show_sold_albums"       collector={collector} onSaved={onSaved} label="Show sold albums"             testid="toggle-show_sold_albums" />
        <ToggleRow k="show_archived_albums"   collector={collector} onSaved={onSaved} label="Show archived albums"         testid="toggle-show_archived_albums" />
      </div>
    </Card>
  );
}

function ToggleRow({ k, collector, onSaved, label, hint, testid }) {
  const on = !!collector?.[k];
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    try {
      await api.patch("/me/profile/privacy", { [k]: !on });
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update.");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={flip} disabled={busy} data-testid={testid} aria-pressed={on}
            className={`text-left rounded-xl border-2 border-[var(--ink)] px-4 py-3 flex items-start gap-3 transition ${
              on ? "bg-[var(--panini-yellow-soft)]" : "bg-white"
            }`}>
      <div className={`mt-0.5 w-10 h-6 rounded-full border-2 border-[var(--ink)] relative shrink-0 ${on ? "bg-[var(--panini-red)]" : "bg-[var(--stone)]"}`}>
        <div className={`absolute top-0 left-0 w-5 h-5 rounded-full bg-white border-2 border-[var(--ink)] transition-transform ${on ? "translate-x-4" : ""}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold">{label}</div>
        {hint && <div className="text-[11px] text-[var(--smoke)] mt-0.5">{hint}</div>}
      </div>
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// Shared bits
// ═════════════════════════════════════════════════════════════
function Card({ children, testid }) {
  return (
    <section data-testid={testid} className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[4px_4px_0_var(--ink)]">
      {children}
    </section>
  );
}

function CardHeader({ Icon, kicker, title, subtitle }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} className="text-[var(--panini-red)]" />}
        <div className="kicker kicker-black">{kicker}</div>
      </div>
      <h2 className="font-display text-2xl mt-1">{title}</h2>
      {subtitle && <p className="text-xs text-[var(--smoke)] mt-1 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function TextInput({ value, onChange, testid, placeholder, maxLength }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
    />
  );
}

function extractIg(url) {
  if (!url) return "";
  try {
    const m = String(url).match(/instagram\.com\/([a-zA-Z0-9._]+)/);
    return m ? m[1] : "";
  } catch { return ""; }
}


/**
 * Signup Redesign · CX Gate — the approved 17-row collecting-since
 * dropdown, shared between the onboarding Profile step and Profile
 * Settings. Values match the enum accepted by PATCH /me/profile.
 * `""` (empty) is the "Prefer not to say" sentinel.
 */
export const COLLECTING_SINCE_OPTIONS = [
  { value: "",                    label: "Prefer not to say" },
  { value: "just_started_2026",   label: "Just started — 2026" },
  { value: "since_2022",          label: "Since 2022" },
  { value: "since_2018",          label: "Since 2018" },
  { value: "since_2014",          label: "Since 2014" },
  { value: "since_2010",          label: "Since 2010" },
  { value: "since_2006",          label: "Since 2006" },
  { value: "since_2002",          label: "Since 2002" },
  { value: "since_1998",          label: "Since 1998" },
  { value: "since_1994",          label: "Since 1994" },
  { value: "since_1990",          label: "Since 1990" },
  { value: "since_1986",          label: "Since 1986" },
  { value: "since_1982",          label: "Since 1982" },
  { value: "since_1978",          label: "Since 1978" },
  { value: "since_1974",          label: "Since 1974" },
  { value: "since_1970",          label: "Since 1970" },
  { value: "not_sure",            label: "Not sure" },
];

function CollectingSinceSelect({ value, onChange }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid="profile-collecting-since"
      className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
    >
      {COLLECTING_SINCE_OPTIONS.map((o) => (
        <option key={o.value || "prefer_not_to_say"} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
